import type { BrandingData } from "@saas/shared/types/brand";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { organizationListQueryKey } from "../lib/api";
import { type BrandingUpdate, mergeBrandingUpdate } from "../lib/branding-update";
import { useActiveOrganization } from "./use-active-organization";

/**
 * Fetch organization branding settings
 * @param organizationId - ID of the organization
 * @returns Promise<BrandingData> - The branding data
 */
async function fetchBrandingSettings(organizationId: string): Promise<BrandingData> {
  const response = await fetch(`/api/organization-branding/${organizationId}`, {
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    if (response.status === 401) {
      // Authentication error will be handled by the global interceptor
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Session expired. Please refresh the page.");
    }
    throw new Error(`Failed to fetch branding settings (${response.status})`);
  }

  const data = await response.json();
  return data.branding;
}

/**
 * Update organization branding settings
 * @param organizationId - ID of the organization
 * @param brandingData - The branding data to update
 * @returns Promise<BrandingData> - The updated branding data
 */
async function updateBrandingSettings(
  organizationId: string,
  brandingData: BrandingData
): Promise<BrandingData> {
  const response = await fetch(`/api/organization-branding/${organizationId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(brandingData),
  });

  if (!response.ok) {
    if (response.status === 401) {
      // Authentication error will be handled by the global interceptor
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Session expired. Please refresh the page.");
    }
    throw new Error(`Failed to update branding settings (${response.status})`);
  }

  const data = await response.json();
  return data.branding;
}

/**
 * Hook for managing organization branding settings
 * Provides functionality to fetch and update branding data
 */
export function useOrganizationBranding() {
  const { activeOrganization, refetchActiveOrganization } = useActiveOrganization();
  const queryClient = useQueryClient();

  // Query for fetching branding settings
  const {
    data: brandingData,
    isLoading,
    error,
    refetch: refetchBranding,
  } = useQuery({
    queryKey: ["organization-branding", activeOrganization?.id],
    queryFn: () => fetchBrandingSettings(activeOrganization?.id || ""),
    enabled: !!activeOrganization?.id,
  });

  // Mutation for updating branding settings
  const updateBrandingMutation = useMutation({
    mutationFn: (brandingData: BrandingData) =>
      updateBrandingSettings(activeOrganization?.id || "", brandingData),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ["organization-branding", activeOrganization?.id],
      });
      queryClient.invalidateQueries({
        queryKey: organizationListQueryKey,
      });
      refetchActiveOrganization();
    },
  });

  /**
   * Update branding settings with automatic cache invalidation.
   * The change is merged onto the saved branding: omitted fields are kept and `null` clears a field.
   * @param update - The branding fields to change
   */
  const updateBranding = async (update: BrandingUpdate) => {
    return updateBrandingMutation.mutateAsync(mergeBrandingUpdate(brandingData, update));
  };

  return {
    brandingData,
    isLoading,
    error,
    refetchBranding,
    updateBranding,
    isUpdating: updateBrandingMutation.isPending,
    updateError: updateBrandingMutation.error,
  };
}
